import React, {createContext, PropsWithChildren, useCallback, useContext, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {isEqual, isFunction} from "lodash";
import {useHistory} from "react-router-dom";
import * as actions from '../_redux/aiReportsActions';
import {useAiReportsContext} from "./AiReportsContext";
import * as invoiceOrdersActions from "../../InvoiceOrders/_redux/invoiceOrdersActions";

type SavedQueriesContext = {
  loading: boolean;
  totalCount: number;
  entities: never[] | null;
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => void;
  deleteSavedItem: (reportId: string) => Promise<void>
}

export interface QueryParams {
  [key: string]: string | number | undefined;
}

type RootState = {
  aiReports: {
    canAnswer: boolean;
    reason: string;
    columns: { field: string; title: string; }[];
    listLoading: boolean;
    totalCount: number;
    entities: never[];
    reportId: string;
  };
};

interface SavedQueriesProviderProps extends PropsWithChildren<{}> {
}

const SavedQueriesContext = createContext<SavedQueriesContext>({
  loading: false,
  totalCount: 0,
  entities: null,
  queryParams: {},
  setQueryParamsBase: () => {
  },
  setQueryParams: () => {
  },
  deleteSavedItem: async () => {
  }
});

export function useSavedQueriesContext() {
  return useContext(SavedQueriesContext);
}

export function SavedQueriesProvider({children}: SavedQueriesProviderProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});
  const setQueryParams = useCallback(
    (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => {
      setQueryParamsBase(prevQueryParams => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }

        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }

        for (let key in nextQueryParams) {
          if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) {
            delete nextQueryParams[key];
          }
        }

        const path = history.location.pathname;
        const newQueryParams = new URLSearchParams(nextQueryParams as Record<string, string>);
        history.replace(`${path}?${newQueryParams.toString()}`);

        return nextQueryParams;
      });
    },
    [history]
  );
  const {listLoading, totalCount, entities} = useSelector(
    (state: RootState) => ({
      listLoading: state.aiReports.listLoading,
      totalCount: state.aiReports.totalCount,
      entities: state.aiReports.entities,
    }),
    shallowEqual
  );

  const updateTable = async () => {
    await dispatch(actions.getQueriesList(queryParams));
  };

  const deleteSavedItem = async (reportId: string) => {
    try {
      const errorCode = await actions.deleteQuery(reportId);
      if (errorCode === 0) {
        await updateTable();
      }
    } catch (e: unknown) {
      console.error(`Error + ${e}`)
    }
  };

  return (
    <SavedQueriesContext.Provider value={{
      loading: listLoading,
      totalCount,
      entities,
      queryParams: {...queryParams},
      setQueryParamsBase,
      setQueryParams,
      deleteSavedItem
    }}>
      {children}
    </SavedQueriesContext.Provider>
  );
}